import React, { useCallback, useContext, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { fareTypes, fareCabins, FlightsClassTypes, FlightsFareTypes } from 'constants/flight'
import { FlightDropdownOption } from 'components/Flights/types'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FareDropdown from './FareDropdown/FareDropdown'
import FlightSearchAirportInputs from './FlightSearchAirportInputs'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import FlightSearchDateSelect from './FlightSearchDateSelect'
import FlightSearchTravellerSelect from './FlightSearchTravellerSelect'
import { flightsEconomyFareClassSelect, flightsPremiumFareClassSelect, flightsBusinessFareClassSelect, flightsFirstFareClassSelect, flightsOneWayFareTypeSelect, flightsReturnFareTypeSelect } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'

const InputsWrapper = styled.form`
  display: none;

  ${mediaQueryUp.sm} {
    position: relative;
    display: grid;
    gap: ${rem(8)};
    grid-template-areas:
      "location-input location-input location-input"
      "date-input traveller-input actions";
    grid-template-columns: 1fr ${rem(178)} ${rem(112)};
  }

  ${mediaQueryUp.md} {
    grid-template-areas: "location-input date-input traveller-input actions";
    grid-template-columns: 1fr minmax(${rem(230)}, ${rem(304)}) minmax(${rem(150)}, ${rem(180)}) ${rem(112)};
  }

  > .location-input-area {
    grid-area: location-input;
  }
  > .date-input-area {
    grid-area: date-input;
  }
  > .traveller-input-area {
    grid-area: traveller-input;
  }
  > .actions-area {
    grid-area: actions;
  }
`

const SearchButton = styled(TextButton)`
  height: auto;
`

interface Props {
  onSearchAirport: () => void;
}

function FlightSearchWidgetDesktop(props: Props) {
  const { onSearchAirport } = props
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const { fareType, fareCabin } = state

  const changeFareType = useCallback((fareType: FlightDropdownOption) => {
    dispatch({ type: FlightSearchWidgetActions.SET_FARE_TYPE, fareType })
    if (fareType.value === FlightsFareTypes.ONE_WAY) {
      fireInteractionEvent(flightsOneWayFareTypeSelect())
    } else if (fareType.value === FlightsFareTypes.RETURN) {
      fireInteractionEvent(flightsReturnFareTypeSelect())
    }
  }, [dispatch])

  const changeFareCabin = useCallback((fareCabin: FlightDropdownOption) => {
    dispatch({ type: FlightSearchWidgetActions.SET_FARE_CABIN, fareCabin })
    switch (fareCabin.value) {
      case FlightsClassTypes.ECONOMY:
        fireInteractionEvent(flightsEconomyFareClassSelect())
        break
      case FlightsClassTypes.PREMIUM:
        fireInteractionEvent(flightsPremiumFareClassSelect())
        break
      case FlightsClassTypes.BUSINESS:
        fireInteractionEvent(flightsBusinessFareClassSelect())
        break
      case FlightsClassTypes.FIRST:
        fireInteractionEvent(flightsFirstFareClassSelect())
        break
      default:
        break
    }
  }, [dispatch])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSearchAirport()
  }

  const formRef = useRef<HTMLFormElement>(null)

  return (
    <Group direction="vertical" gap={16}>
      <Group direction="horizontal" gap={8}>
        <FareDropdown
          options={fareTypes}
          selectedOption={fareType}
          onOptionChange={changeFareType}
          data-testid="fare-type-dropdown"
        />
        <FareDropdown
          options={fareCabins}
          selectedOption={fareCabin}
          onOptionChange={changeFareCabin}
          data-testid="fare-class-dropdown"
        />
      </Group>
      <InputsWrapper onSubmit={onSubmit} ref={formRef}>
        <FlightSearchAirportInputs className="location-input-area" />
        <FlightSearchDateSelect className="date-input-area" anchorRef={formRef} />
        <FlightSearchTravellerSelect className="traveller-input-area" anchorRef={formRef} />
        <SearchButton
          className="actions-area"
          kind="primary" size="large"
          data-testid="airportSearchButton"
          type="submit"
        >
          Search
        </SearchButton>
      </InputsWrapper>
    </Group>
  )
}

export default FlightSearchWidgetDesktop
